import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../../Components/Layout";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrumb from "../Component/Breadcrumb";
import Sidebar from "../../Dashboard/Component/Siderbar";
import { Formik, Form, Field } from "formik";
import { userValidationSchema } from "./validationSchema";
import config from "../../../config";
import { FaUser, FaPhone, FaEnvelope, FaLock, FaBirthdayCake, FaVenusMars, FaImage, FaPercentage } from 'react-icons/fa';

export default function Add() {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = JSON.parse(localStorage.getItem("user_data"));
  const [previewImage, setPreviewImage] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const initialValues = {
    name: "",
    surname: "",
    username: "",
    phone: "",
    email: "",
    password: "",
    markup: 0,
    profile_photo: null,
  };

  // Cleanup function for image preview
  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const url = `${config.API_BASE_URL_V1}agency/user/create`;
      const formDataToSend = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value !== null && value !== '') {
          formDataToSend.append(key, value);
        }
      });

      const response = await axios({
        method: 'post',
        url: url,
        data: formDataToSend,
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        Swal.fire({
          title: "User Created Successfully!",
          icon: "success",
          draggable: true,
        });
        navigate("/dashboard-users");
        window.scrollTo(0, 0);
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setErrorMessage(errorMessage);
      
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Breadcrumb title="Add User" />
      <div className="content">
        <div className="container">
          <div className="row">
            <Sidebar>
              <div className="col-12">
                {errorMessage && (
                  <div className="alert alert-danger" role="alert">
                    {errorMessage}
                  </div>
                )}
                <Formik
                  initialValues={initialValues}
                  validationSchema={userValidationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ 
                    values, 
                    errors, 
                    touched, 
                    handleChange, 
                    handleBlur, 
                    setFieldValue,
                    isSubmitting 
                  }) => (
                    <Form>
                      <div className="card shadow-sm border-0 rounded-lg">
                        <div className="card-header bg-primary py-3">
                          <h5 className="fs-18 mb-0 text-white">Add New User</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-4 mb-4">
                              <div className="profile-image-container text-center">
                                <div className="profile-image-wrapper mb-3">
                                  <img
                                    src={previewImage || "assets/img/users/user-lg-26.jpg"}
                                    alt="Profile"
                                    className="rounded-circle profile-image"
                                  />
                                </div>
                                <div className="custom-file-upload">
                                  <label className="form-label d-flex align-items-center justify-content-center">
                                    <FaImage className="me-2" />
                                    Upload Profile Image
                                  </label>
                                  <input
                                    type="file"
                                    className={`form-control ${touched.profile_photo && errors.profile_photo ? 'is-invalid' : ''}`}
                                    name="profile_photo"
                                    onChange={(event) => {
                                      const file = event.currentTarget.files[0];
                                      setFieldValue("profile_photo", file);
                                      if (file) {
                                        if (previewImage) {
                                          URL.revokeObjectURL(previewImage);
                                        }
                                        setPreviewImage(URL.createObjectURL(file));
                                      }
                                    }}
                                    onBlur={handleBlur}
                                  />
                                  {touched.profile_photo && errors.profile_photo && (
                                    <div className="invalid-feedback">{errors.profile_photo}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="col-md-8">
                              <div className="row">
                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaUser className="me-2" />
                                    Name
                                  </label>
                                  <Field
                                    type="text"
                                    className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                    name="name"
                                  />
                                  {touched.name && errors.name && (
                                    <div className="invalid-feedback">{errors.name}</div>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaUser className="me-2" />
                                    Surname
                                  </label>
                                  <Field
                                    type="text"
                                    className={`form-control ${touched.surname && errors.surname ? 'is-invalid' : ''}`}
                                    name="surname"
                                  />
                                  {touched.surname && errors.surname && (
                                    <div className="invalid-feedback">{errors.surname}</div>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaUser className="me-2" />
                                    Username
                                  </label>
                                  <Field
                                    type="text"
                                    className={`form-control ${touched.username && errors.username ? 'is-invalid' : ''}`}
                                    name="username"
                                  />
                                  {touched.username && errors.username && (
                                    <div className="invalid-feedback">{errors.username}</div>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaPhone className="me-2" />
                                    Phone
                                  </label>
                                  <Field
                                    type="text"
                                    className={`form-control ${touched.phone && errors.phone ? 'is-invalid' : ''}`}
                                    name="phone"
                                  />
                                  {touched.phone && errors.phone && (
                                    <div className="invalid-feedback">{errors.phone}</div>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaEnvelope className="me-2" />
                                    Email
                                  </label>
                                  <Field
                                    type="email"
                                    className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                    name="email"
                                  />
                                  {touched.email && errors.email && (
                                    <div className="invalid-feedback">{errors.email}</div>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaLock className="me-2" />
                                    Password
                                  </label>
                                  <Field
                                    type="password"
                                    className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                                    name="password"
                                  />
                                  {touched.password && errors.password && (
                                    <div className="invalid-feedback">{errors.password}</div>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label d-flex align-items-center">
                                    <FaPercentage className="me-2" />
                                    Markup (%)
                                  </label>
                                  <Field
                                    type="number"
                                    className={`form-control ${touched.markup && errors.markup ? 'is-invalid' : ''}`}
                                    name="markup"
                                    min={0}
                                    max={100}
                                  />
                                  {touched.markup && errors.markup && (
                                    <div className="invalid-feedback">{errors.markup}</div>
                                  )}
                                </div>

                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="card-footer bg-light py-3">
                          <div className="d-flex justify-content-end">
                            <button
                              type="submit"
                              className="btn btn-primary px-4"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              ) : null}
                              {isSubmitting ? "Creating..." : "Create User"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </Sidebar>
          </div>
        </div>
      </div>

      <style jsx>{`
        .profile-image-container {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
        }
        .profile-image-wrapper {
          width: 150px;
          height: 150px;
          margin: 0 auto;
          position: relative;
        }
        .profile-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: 3px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .custom-file-upload {
          position: relative;
          overflow: hidden;
        }
        .custom-file-upload input[type="file"] {
          position: absolute;
          top: 0;
          right: 0;
          min-width: 100%;
          min-height: 100%;
          font-size: 100px;
          text-align: right;
          filter: alpha(opacity=0);
          opacity: 0;
          outline: none;
          background: white;
          cursor: inherit;
          display: block;
        }
        .form-control:focus {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
        }
        .card {
          transition: all 0.3s ease;
        }
        .card:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </Layout>
  );
}
